import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import { usePageSEO } from '../hooks/useSEO';
import { useLanguage, LanguageSwitcher } from '../i18n/LanguageContext';

const LandingPage: React.FC = () => {
  usePageSEO('home');
  const navigate = useNavigate();
  const { dict } = useLanguage();
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => setActiveFAQ(activeFAQ === index ? null : index);
  const goApp = () => navigate('/app');

  return (
    <div className="landing-page" style={{ position: 'relative' }}>
      {/* Top-right language switch */}
      <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
        <LanguageSwitcher />
      </div>

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-brand">
              <span className="brand-icon">🧠</span>
              <span className="brand-text">StudyMind</span>
            </div>
            <h1 className="hero-title">
              {dict.landing.heroTitleA}
              <span className="gradient-text">{dict.landing.heroTitleB}</span>
            </h1>
            <p className="hero-subtitle">{dict.landing.heroSubtitle}</p>
            <div className="hero-actions">
              <button className="btn btn-primary btn-large" onClick={goApp}>
                <span className="btn-icon">🚀</span>
                {dict.landing.getStarted}
              </button>
              <button
                className="btn btn-secondary btn-large"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <span className="btn-icon">✨</span>
                {dict.landing.learnMore}
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-cards">
              {[
                ['🎯', dict.dashboard.modules.find((m) => m.id === 'dictation')?.name ?? 'Dictation', 'card-1'],
                ['✍️', dict.dashboard.modules.find((m) => m.id === 'writing')?.name ?? 'Writing', 'card-2'],
                ['🧠', dict.dashboard.modules.find((m) => m.id === 'understanding')?.name ?? 'Understanding', 'card-3'],
                ['🎧', dict.dashboard.modules.find((m) => m.id === 'listening')?.name ?? 'Listening', 'card-4'],
                ['📝', dict.dashboard.modules.find((m) => m.id === 'notes')?.name ?? 'Notes', 'card-5'],
                ['🗣️', dict.dashboard.modules.find((m) => m.id === 'speaking')?.name ?? 'Speaking', 'card-6'],
                ['📖', dict.dashboard.modules.find((m) => m.id === 'lecture')?.name ?? 'Lecture', 'card-7'],
                ['📚', dict.dashboard.modules.find((m) => m.id === 'recite')?.name ?? 'Flashcards', 'card-8'],
                ['🎓', dict.dashboard.modules.find((m) => m.id === 'exam')?.name ?? 'Exam', 'card-9'],
              ].map(([icon, label, cls]) => (
                <div key={cls} className={`floating-card ${cls}`}>
                  <span className="card-icon">{icon}</span>
                  <span className="card-text">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {dict.landing.featuresTitleA}
              <span className="gradient-text">{dict.landing.featuresTitleB}</span>
            </h2>
            <p className="section-subtitle">{dict.landing.featuresSubtitle}</p>
          </div>
          <div className="features-grid">
            {dict.landing.features.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
                onClick={goApp}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goApp(); } }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {dict.landing.stepsTitleA}
              <span className="gradient-text">{dict.landing.stepsTitleB}</span>
            </h2>
            <p className="section-subtitle">{dict.landing.stepsSubtitle}</p>
          </div>
          <div className="steps-container">
            {dict.landing.steps.map((step, i) => (
              <div key={i} className="step">
                <div className="step-number">{i + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {dict.landing.faqTitleA}
              <span className="gradient-text">{dict.landing.faqTitleB}</span>
            </h2>
            <p className="section-subtitle">{dict.landing.faqSubtitle}</p>
          </div>
          <div className="faq-container">
            {dict.landing.faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button className="faq-question" onClick={() => toggleFAQ(index)}>
                  <span>{faq.q}</span>
                  <svg
                    className={`faq-icon ${activeFAQ === index ? 'active' : ''}`}
                    width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className={`faq-answer ${activeFAQ === index ? 'active' : ''}`}>
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              {dict.landing.ctaTitleA}
              <span className="gradient-text">{dict.landing.ctaTitleB}</span>
            </h2>
            <p className="cta-subtitle">{dict.landing.ctaSubtitle}</p>
            <button className="btn btn-primary btn-large" onClick={goApp}>
              <span className="btn-icon">🎯</span>
              {dict.landing.ctaButton}
            </button>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <span className="footer-logo">🧠</span>
              <span className="footer-text">StudyMind</span>
            </div>
            <p className="footer-copyright">{dict.landing.rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
